import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { type StatementItem } from '@webapp/modules/accounts-payable/entities/StatementItem'
import { useUnmatchedStatementItemListPresenter } from './useUnmatchedStatementItemListPresenter'
import { UnmatchedStatementItemsPane } from '../../views/UnmatchedStatementItemsPane'

export interface IUnmatchedStatementItemListPresenterProps {
  accountId: number
  /** Suggested-match filter is triggered by the sibling CC pane — FS-owned (BHV-037) */
  filterByCcItemId: Nullable<number>
  onClearFilter: () => void
  /** Selection bridge — the FS computes selection variance (ADR-9, BHV-031) */
  selectedItemIds: number[]
  onSelectionChange: (items: StatementItem[]) => void
  /** FS tracks recently created/duplicated rows (BHV-041/042) */
  justCreatedItemIds: number[]
  /** Slot for the Duplicate / Remove menu (ADR-9) */
  renderRowMenu: (item: StatementItem) => ReactNode
}

export const UnmatchedStatementItemListPresenter = ({
  accountId,
  filterByCcItemId,
  onClearFilter,
  selectedItemIds,
  onSelectionChange,
  justCreatedItemIds,
  renderRowMenu,
}: IUnmatchedStatementItemListPresenterProps) => {
  const {
    data: { items },
    states: { isLoading },
  } = useUnmatchedStatementItemListPresenter({ accountId, filterByCcItemId })

  // Pool of every item seen (survives pagination/filtering) so selected ids
  // can always be resolved back to entities for the FS selection bridge.
  const poolRef = useRef(new Map<number, StatementItem>())
  useEffect(() => {
    items.forEach((item) => poolRef.current.set(item.id, item))
  }, [items])

  const handleRowClick = useCallback(
    (item: StatementItem) => {
      const nextIds = selectedItemIds.includes(item.id)
        ? selectedItemIds.filter((id) => id !== item.id)
        : [...selectedItemIds, item.id]

      onSelectionChange(
        nextIds.reduce<StatementItem[]>((selected, id) => {
          const entity = poolRef.current.get(id)
          return entity ? [...selected, entity] : selected
        }, [])
      )
    },
    [onSelectionChange, selectedItemIds]
  )

  return (
    <UnmatchedStatementItemsPane
      items={items}
      isLoading={isLoading}
      selectedItemIds={selectedItemIds}
      justCreatedItemIds={justCreatedItemIds}
      isFiltered={filterByCcItemId !== null}
      onClearFilter={onClearFilter}
      onRowClick={handleRowClick}
      renderRowMenu={renderRowMenu}
    />
  )
}

UnmatchedStatementItemListPresenter.displayName =
  'UnmatchedStatementItemListPresenter'
